import { memo, useCallback } from 'react';
import { Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { EDIT_SCOPE } from '../../constants/editScope';
import { useEditorSession } from '../../hooks/useEditorSession';
import { usePermissionDraft } from '../../hooks/usePermissionDraft';
import { PermissionTree } from '../shared/PermissionTree';
import ScopeBanner from '../shared/ScopeBanner';
import UnsavedChangesModal from '../shared/UnsavedChangesModal';

/**
 * Edit mode of one studio of a custom role: the "Changes apply to all members with this role"
 * banner, the editable permission tree and Save changes / Discard (asks first when there are
 * unsaved edits), plus Remove access when the role has permissions in the studio. `onSave`
 * resolves `true` when saved (the editor then closes); on failure the edits are kept.
 *
 * @param {object} props
 * @param {object} props.studio Catalog studio with its `tree`.
 * @param {object[]} props.grants The role's `{ permission_id, level }` grants in this studio.
 * @param {(studio: object, grants: object[]) => Promise<boolean>} props.onSave
 * @param {() => void} props.onClose Leaves edit mode (after a save, or on Discard).
 * @param {(studio: object) => void} props.onRemoveAccess
 */
const RoleStudioEditor = ({ studio, grants, onSave, onClose, onRemoveAccess }) => {
  const { t } = useTranslation('people-access');
  const { draft, tags, isDirty, grants: draftGrants, toggle, changeLevel } = usePermissionDraft(studio.tree, grants);

  const saveGrants = useCallback(() => onSave(studio, draftGrants), [onSave, studio, draftGrants]);
  const { isSaving, isConfirmingDiscard, save, cancel, keepEditing, discard } = useEditorSession({
    isDirty,
    onSave: saveGrants,
    onClose,
  });
  const handleRemove = useCallback(() => onRemoveAccess(studio), [onRemoveAccess, studio]);

  return (
    <div className="flex flex-col gap-3">
      <ScopeBanner scope={EDIT_SCOPE.ROLE} />
      <PermissionTree
        nodes={studio.tree}
        draft={draft}
        mode="edit"
        tags={tags}
        expand="top"
        onToggle={toggle}
        onLevelChange={changeLevel}
      />
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={!isDirty} loading={isSaving}>
            {t('roles.detail.permissions.save')}
          </Button>
          <Button variant="secondary" size="sm" onClick={cancel} disabled={isSaving}>
            {t('roles.detail.permissions.discard')}
          </Button>
        </div>
        {grants.length ? (
          <Button variant="danger" size="sm" onClick={handleRemove} disabled={isSaving}>
            {t('roles.detail.permissions.removeAccess')}
          </Button>
        ) : null}
      </div>
      <UnsavedChangesModal isOpen={isConfirmingDiscard} onKeepEditing={keepEditing} onDiscard={discard} />
    </div>
  );
};

export default memo(RoleStudioEditor);
